import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, Tooltip, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import userService from '../services/userService';
import workoutService from '../services/workoutService';

const PAGE_SIZE = 20;

const ManageAthletes = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [total, setTotal] = useState(0);
  const [acting, setActing] = useState({});

  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [filterBy, setFilterBy] = useState('name');
  const [filterStatusBy, setFilterStatusBy] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [page, setPage] = useState(1);
  // '' = default API sort; 'desc' / 'asc' = sort by instagramFollowers
  const [followersSort, setFollowersSort] = useState('');

  const [editingRevenueId, setEditingRevenueId] = useState(null);
  const [newRevenueValue, setNewRevenueValue] = useState('');
  const [editingFollowersId, setEditingFollowersId] = useState(null);
  const [newFollowersValue, setNewFollowersValue] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [newNoteValue, setNewNoteValue] = useState('');

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [modalMediaUrl, setModalMediaUrl] = useState('');

  const fetchAthletes = useCallback(async (pageNum = 1, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const {
        keyword = appliedQuery,
        filterField = filterBy,
        status = filterStatusBy,
        dateStart = dateFilterStart,
        dateEnd = dateFilterEnd,
        sortFollowers = followersSort,
      } = filters;

      const { items, total: totalCount } = await userService.getTrainers({
        skip: (pageNum - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        keyword,
        filterBy: filterField,
        status,
        dateStart,
        dateEnd,
        ...(sortFollowers
          ? { sortBy: 'instagramFollowers', sortOrder: sortFollowers }
          : {}),
      });

      setAthletes(items);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to fetch athletes: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, filterBy, filterStatusBy, dateFilterStart, dateFilterEnd, followersSort]);

  useEffect(() => {
    dispatch(login(user));
    fetchAthletes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccept = async (userId) => {
    setActing((p) => ({ ...p, [userId]: true }));
    try {
      await userService.acceptTrainer(userId);
      await fetchAthletes(page);
    } catch (err) {
      alert('Failed to accept athlete: ' + (err?.message || 'Unknown error'));
    } finally {
      setActing((p) => ({ ...p, [userId]: false }));
    }
  };

  const handleDecline = async (userId) => {
    if (!window.confirm('Decline this request?')) return;
    setActing((p) => ({ ...p, [userId]: true }));
    try {
      await userService.declineTrainer(userId);
      await fetchAthletes(page);
    } catch (err) {
      alert('Failed to decline athlete: ' + (err?.message || 'Unknown error'));
    } finally {
      setActing((p) => ({ ...p, [userId]: false }));
    }
  };

  const performSearch = () => {
    const trimmed = (query || '').trim();
    setAppliedQuery(trimmed);
    fetchAthletes(1, {
      keyword: trimmed,
      filterField: filterBy,
      status: filterStatusBy,
      dateStart: dateFilterStart,
      dateEnd: dateFilterEnd,
    });
  };

  const handleRemoveFilter = () => {
    setQuery('');
    setAppliedQuery('');
    setFilterStatusBy('all');
    setDateFilterStart('');
    setDateFilterEnd('');
    fetchAthletes(1, {
      keyword: '',
      filterField: filterBy,
      status: 'all',
      dateStart: '',
      dateEnd: '',
    });
  };

  const isFilteredActive = () => {
    return (appliedQuery || '').trim() || dateFilterStart || dateFilterEnd || filterStatusBy !== 'all';
  };

  const handlePageChange = (_, v) => {
    fetchAthletes(v);
  };

  const handleFollowersSort = () => {
    // Cycle: default → desc (high first) → asc (low first) → default
    const next =
      followersSort === '' ? 'desc' : followersSort === 'desc' ? 'asc' : '';
    setFollowersSort(next);
    fetchAthletes(1, { sortFollowers: next });
  };

  const exportExcel = async () => {
    try {
      const { items } = await userService.getTrainers({
        skip: 0,
        limit: Math.min(Math.max(total, PAGE_SIZE), 5000),
        keyword: appliedQuery,
        filterBy,
        status: filterStatusBy,
        dateStart: dateFilterStart,
        dateEnd: dateFilterEnd,
        ...(followersSort
          ? { sortBy: 'instagramFollowers', sortOrder: followersSort }
          : {}),
      });
      if (!items || items.length === 0) {
        alert('No data to export');
        return;
      }
      const headers = ['No', 'Name', 'Email', 'Phone', 'Instagram', 'Followers', 'Athletes Category', 'Athlete %', 'Agent %', 'Thravos %', 'Status', 'Submitted At / Accepted At', 'Video', 'Note'];
      const rows = items.map((u, index) => {
        const poolPct = u?.role === 'trainer' ? (u.incomePercentage || 80) : null;
        const agentPct = u?.role === 'trainer' && u.agentId && typeof u.agentCommissionPercent === 'number' ? u.agentCommissionPercent : null;
        const athletePct = poolPct !== null ? (agentPct !== null ? poolPct - agentPct : poolPct) : '';
        return [
          index + 1,
          u?.name || u?.fullName || '',
          u?.email || '',
          u?.phone || u?.phoneNumber || '',
          u?.instagramUsername || '',
          u?.instagramFollowers || '',
          u?.categories?.join(', ') || '',
          athletePct,
          agentPct !== null ? agentPct : '',
          poolPct !== null ? 100 - poolPct : '',
          u?.wantToBeTrainer ? 'Pending' : u?.role === 'trainer' ? 'Accepted' : !u?.athleteApproved ? 'Rejected' : '',
          `Submitted: ${u?.trainerApplicationSubmittedAt ? new Date(u.trainerApplicationSubmittedAt).toLocaleString() : ''} | Accepted: ${u?.acceptedAt ? new Date(u.acceptedAt).toLocaleString() : ''}`, u?.applicationUrl ? u.applicationUrl.replace(/^@/, '') : '', u?.note || ''
        ];
      });
      const csvContent = [headers, ...rows].map(e => e.map(v => `"${v}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'athletes.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Failed to export: ' + (err?.message || 'Unknown error'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusChip = (u) => {
    if (u?.wantToBeTrainer) return <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontWeight: 600 }} />;
    if (u?.role === 'trainer') return <Chip label="Accepted" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#81c784', fontWeight: 600 }} />;
    if (u?.athleteApproved === false) return <Chip label="Rejected" size="small" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#e57373', fontWeight: 600 }} />;
    return null;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  if (loading && athletes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: '#2bb6a9' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Field</InputLabel>
          <Select value={filterBy} label="Field" onChange={(e) => setFilterBy(e.target.value)}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="instagram">IG</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatusBy} label="Status" onChange={(e) => {
            const status = e.target.value;
            setFilterStatusBy(status);
            fetchAthletes(1, {
              keyword: appliedQuery,
              filterField: filterBy,
              status,
              dateStart: dateFilterStart,
              dateEnd: dateFilterEnd,
            });
          }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && performSearch()} sx={{ minWidth: 180 }} />
        <Typography variant="caption" sx={{ color: '#8b949e', mx: 0.5 }}>Accepted:</Typography>
        <TextField size="small" type="date" value={dateFilterStart} onChange={(e) => {
          const dateStart = e.target.value;
          setDateFilterStart(dateStart);
          fetchAthletes(1, {
            keyword: appliedQuery,
            filterField: filterBy,
            status: filterStatusBy,
            dateStart,
            dateEnd: dateFilterEnd,
          });
        }} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Typography variant="caption" sx={{ color: '#8b949e' }}>–</Typography>
        <TextField size="small" type="date" value={dateFilterEnd} onChange={(e) => {
          const dateEnd = e.target.value;
          setDateFilterEnd(dateEnd);
          fetchAthletes(1, {
            keyword: appliedQuery,
            filterField: filterBy,
            status: filterStatusBy,
            dateStart: dateFilterStart,
            dateEnd,
          });
        }} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={performSearch}>Search</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleRemoveFilter} disabled={!isFilteredActive()}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel}>Export</Button>
        <IconButton size="small" onClick={() => fetchAthletes(page)} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.25)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#2bb6a9' }} />
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Instagram</TableCell>
                <TableCell
                  onClick={handleFollowersSort}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: '#2bb6a9' },
                  }}
                >
                  <Tooltip
                    arrow
                    title={
                      followersSort === 'desc'
                        ? 'Sorted high → low. Click to sort low → high'
                        : followersSort === 'asc'
                          ? 'Sorted low → high. Click to clear sort'
                          : 'Click to sort by Instagram followers (high → low)'
                    }
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      Followers
                      {followersSort === 'desc' ? (
                        <ArrowDownwardIcon sx={{ fontSize: 14, color: '#2bb6a9' }} />
                      ) : followersSort === 'asc' ? (
                        <ArrowUpwardIcon sx={{ fontSize: 14, color: '#2bb6a9' }} />
                      ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 14, color: '#8b949e' }} />
                      )}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Income Split</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ lineHeight: 1.2, whiteSpace: 'nowrap', py: 0.75 }}>
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2 }}>Submitted At</Box>
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2 }}>/ Accepted At</Box>
                </TableCell>
                <TableCell>ID Verified</TableCell>
                <TableCell>Earning Mode</TableCell>
                <TableCell>Residency</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Video</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {athletes.length === 0 ? (
                <TableRow><TableCell colSpan={16} align="center" sx={{ py: 4, color: '#8b949e' }}>No athletes found.</TableCell></TableRow>
              ) : (
                athletes.map((u, index) => {
                  const name = u?.name || u?.fullName || '—';
                  const email = u?.email || '—';
                  const phone = u?.phone || u?.phoneNumber || '—';
                  const ig = (u?.instagramUsername || '').replace(/^@/, '');
                  const igUrl = ig ? `https://instagram.com/${ig}` : '';
                  const ag = (u?.applicationUrl || '').replace(/^@/, '');
                  const dateOpts = { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
                  const submittedAtDisplay = u?.trainerApplicationSubmittedAt
                    ? new Date(u.trainerApplicationSubmittedAt).toLocaleString(undefined, dateOpts)
                    : '';
                  const acceptedAtDisplay = u?.acceptedAt
                    ? new Date(u.acceptedAt).toLocaleString(undefined, dateOpts)
                    : '';

                  return (
                    <TableRow key={u._id}>
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={email}><Typography variant="body2" sx={{ fontSize: '0.8rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#e4e6eb' }}>{email}</Typography></Tooltip>
                          {email !== '—' && <IconButton size="small" onClick={() => copyToClipboard(email)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {phone !== '—' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#e4e6eb' }}>{phone.length > 8 ? phone.slice(0, 8) + '…' : phone}</Typography>
                            <IconButton size="small" onClick={() => copyToClipboard(phone)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {ig ? <a href={igUrl} target="_blank" rel="noreferrer" style={{ color: '#2bb6a9', fontSize: '0.8rem', textDecoration: 'none' }}>@{ig.length > 12 ? ig.slice(0, 12) + '…' : ig}</a> : '—'}
                      </TableCell>

                      {/* Followers */}
                      <TableCell>
                        {editingFollowersId === u._id ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField size="small" type="number" value={newFollowersValue} onChange={(e) => setNewFollowersValue(e.target.value)} sx={{ width: 80 }} inputProps={{ min: 0 }} />
                            <IconButton size="small" color="success" onClick={async () => {
                              try {
                                await userService.updateInstagramFollowers(u._id, newFollowersValue);
                                setAthletes((prev) => prev.map((a) => a._id === u._id ? { ...a, instagramFollowers: newFollowersValue } : a));
                                setEditingFollowersId(null);
                              } catch (err) { alert('Failed: ' + (err?.message || 'Unknown error')); }
                            }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                            <IconButton size="small" onClick={() => setEditingFollowersId(null)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{u?.instagramFollowers ? Number(u.instagramFollowers).toLocaleString() : '—'}</span>
                            <IconButton size="small" onClick={() => { setEditingFollowersId(u._id); setNewFollowersValue(u?.instagramFollowers || ''); }} sx={{ color: '#8b949e', p: 0.3 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                          </Box>
                        )}
                      </TableCell>

                      <TableCell sx={{ fontSize: '0.8rem' }}>{u?.categories?.join(', ') || '—'}</TableCell>

                      {/* Income Split */}
                      <TableCell>
                        {u?.role === 'trainer' ? (() => {
                          const poolPct = typeof u.incomePercentage === 'number' && u.incomePercentage > 0 ? u.incomePercentage : 80;
                          const agentCommissionPct = u.agentId && typeof u.agentCommissionPercent === 'number' ? u.agentCommissionPercent : null;
                          const athletePct = agentCommissionPct !== null ? poolPct - agentCommissionPct : poolPct;
                          const platformPct = 100 - poolPct;
                          return editingRevenueId === u._id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField size="small" type="number" value={newRevenueValue} onChange={(e) => setNewRevenueValue(e.target.value)} sx={{ width: 70 }} />
                              <IconButton size="small" color="success" onClick={async () => {
                                try {
                                  const athleteShare = Number(newRevenueValue);
                                  const incomePercentage = agentCommissionPct !== null ? athleteShare + agentCommissionPct : athleteShare;
                                  await workoutService.updateIncomePercentage(u._id, incomePercentage);
                                  setAthletes((prev) => prev.map((a) => a._id === u._id ? { ...a, incomePercentage } : a));
                                  setEditingRevenueId(null);
                                } catch (err) { alert('Failed: ' + (err?.message || 'Unknown error')); }
                              }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ lineHeight: 1.5 }}>
                                <Typography variant="caption" sx={{ display: 'block', color: '#8b949e' }}>Athlete: <strong style={{ color: '#4caf74' }}>{athletePct}%</strong></Typography>
                                {agentCommissionPct !== null && (
                                  <Typography variant="caption" sx={{ display: 'block', color: '#8b949e' }}>Agent: <strong style={{ color: '#4fa3f7' }}>{agentCommissionPct}%</strong></Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block', color: '#8b949e' }}>Thravos: <strong style={{ color: '#f47a6a' }}>{platformPct}%</strong></Typography>
                              </Box>
                              <IconButton size="small" onClick={() => { setEditingRevenueId(u._id); setNewRevenueValue(athletePct); }} sx={{ color: '#8b949e', p: 0.3 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                            </Box>
                          );
                        })() : null}
                      </TableCell>

                      <TableCell>{getStatusChip(u)}</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', py: 0.5, lineHeight: 1.2 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.2, color: '#e4e6eb', whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ color: '#8b949e' }}>Submitted </Box>{submittedAtDisplay}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.2, color: '#e4e6eb', whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ color: '#8b949e' }}>Accepted </Box>{acceptedAtDisplay}
                        </Typography>
                      </TableCell>

                      {/* Compliance columns */}
                      <TableCell>
                        <Chip
                          label={u.identityVerified ? 'Verified' : 'No'}
                          size="small"
                          color={u.identityVerified ? 'success' : 'default'}
                          variant={u.identityVerified ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.earningModeEnabled ? 'Active' : 'Off'}
                          size="small"
                          color={u.earningModeEnabled ? 'success' : 'default'}
                          variant={u.earningModeEnabled ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {u.residencyStatus === 'us_citizen' ? 'US Citizen' : u.residencyStatus === 'foreign_national' ? 'Foreign' : '—'}
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" variant="contained" color="error" onClick={() => handleDecline(u._id)} disabled={acting[u._id] || !(u?.wantToBeTrainer || u?.role === 'trainer')} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>
                            Reject
                          </Button>
                          <Button size="small" variant="contained" color="success" onClick={() => handleAccept(u._id)} disabled={acting[u._id] || !(u?.wantToBeTrainer || (!u?.wantToBeTrainer && u?.athleteApproved === false))} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>
                            Accept
                          </Button>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {ag ? (
                          <IconButton size="small" onClick={() => { setModalMediaUrl(ag); setShowMediaModal(true); }} sx={{ color: '#ff9800' }}>
                            <PlayCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        ) : '—'}
                      </TableCell>

                      {/* Note */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        {editingNoteId === u._id ? (
                          <Box>
                            <TextField size="small" multiline rows={2} fullWidth value={newNoteValue} onChange={(e) => setNewNoteValue(e.target.value)} sx={{ mb: 0.5 }} />
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <IconButton size="small" color="success" onClick={async () => {
                                try {
                                  await userService.updateNote(u._id, newNoteValue);
                                  setAthletes((prev) => prev.map((a) => a._id === u._id ? { ...a, note: newNoteValue || null } : a));
                                  setEditingNoteId(null);
                                } catch (err) { alert('Failed: ' + (err?.message || 'Unknown error')); }
                              }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={() => setEditingNoteId(null)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1, color: '#e4e6eb' }}>{u?.note || '—'}</Typography>
                            <IconButton size="small" onClick={() => { setEditingNoteId(u._id); setNewNoteValue(u?.note || ''); }} sx={{ color: '#8b949e', p: 0.3, flexShrink: 0 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#8b949e' }}>
            Showing {total === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + PAGE_SIZE, total)} of {total} entries
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} size="small"
            sx={{ '& .MuiPaginationItem-root': { color: '#8b949e' }, '& .Mui-selected': { bgcolor: 'rgba(43,182,169,0.15) !important', color: '#2bb6a9' } }} />
        </Box>
      )}

      {/* Video Dialog */}
      <Dialog open={showMediaModal} onClose={() => { setShowMediaModal(false); setModalMediaUrl(''); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Video Preview
          <IconButton onClick={() => { setShowMediaModal(false); setModalMediaUrl(''); }} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {modalMediaUrl && (
            <video controls style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}>
              <source src={modalMediaUrl} />
            </video>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManageAthletes;
